import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { HdBodygraphChartPreset } from '../hd-bodygraph.models';
import { HdChartPanelViewMode } from '../hd-chart-panel-view-mode';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdChartEditDialogComponent } from '../../library/hd-chart-edit-dialog.component';
import { HdChartDocumentResult, HdChartUpdateInput } from '../../library/hd-chart-library.models';
import { HdChartLibraryService } from '../../library/hd-chart-library.service';
import { HdLifeTimelineMarker } from '../../timeline/hd-life-timeline.models';
import { HdMomentsCalendarComponent } from '../../timeline/hd-moments-calendar.component';
import { HdIChingAccessService } from '../../../iching/hd-iching-access.service';
import { HdIChingMomentDialogComponent } from '../../../iching/moment/hd-iching-moment-dialog.component';
import { HdIChingConsultRequest, HdIChingMomentDialogTarget } from '../../../iching/moment/hd-iching-moment.models';
import { HdIChingMomentsService } from '../../../iching/moment/hd-iching-moments.service';
import { HdMomentsExportDialogComponent, HdMomentsExportTarget } from '../../moments-export/hd-moments-export-dialog.component';
import { HdMomentLinkMode, HdMomentLinkDialogComponent, HdMomentLinkTarget } from './hd-moment-link-dialog.component';
import { HdMomentsDialogComponent, HdMomentsDialogTarget } from './hd-moments-dialog.component';
import { HdMomentRowVm, buildHdLifeTimelineMarkers, buildHdMomentRow, buildHdMomentRows } from './hd-moments.models';
import { isHdMindMomentRelation } from './hd-mind-log.models';
import { HD_MIND_MOMENT_CATEGORY } from './hd-moment-fields';

/** Compacte lijst: de recentste N rijen; de rest via "Alle momenten…". */
const COMPACT_ROW_COUNT = 8;

/**
 * Momenten-sectie in het Geavanceerd-paneel (spec notable moments §4.4):
 * het groeiende archief van bewaarde momenten van deze chart — snapshots
 * (hasMoment), cycles/solar returns (hasCycle) en gelinkte charts zoals een
 * kind-chart — chronologisch aflopend. Sibling van de Cycles-sectie (de
 * generator van de zes canonieke cycli); bewust een eigen sectie.
 */
@Component({
  selector: 'hd-moments-section',
  standalone: true,
  imports: [
    HdMomentLinkDialogComponent,
    HdMomentsDialogComponent,
    HdMomentsCalendarComponent,
    HdChartEditDialogComponent,
    HdIChingMomentDialogComponent,
    HdMomentsExportDialogComponent,
  ],
  templateUrl: './hd-moments-section.component.html',
  styleUrl: './hd-moments-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMomentsSectionComponent {
  /** Library-chart-id van de actieve tab; null = niet opgeslagen → eerst opslaan. */
  readonly savedChartId = input<string | null>(null);
  /** Weergavestand (spec 2026-10-20-chart-share §4.5): shared/guest → geen momenten, ook niet ingelogd. */
  readonly viewMode = input<HdChartPanelViewMode>('owner');
  /** Naam van de actieve chart (titel van de dialogen). */
  readonly chartName = input('');
  /** Volledige preset van de actieve tab (bron voor "moment op datum": natal-kern + document). */
  readonly preset = input<HdBodygraphChartPreset | null>(null);
  /** Alle relaties van de chart (geladen door het Geavanceerd-paneel; deze sectie filtert). */
  readonly relations = input<readonly PlatformChartRelationDto[]>([]);
  readonly loading = input(false);
  readonly error = input('');

  /** Klik op een moment: open die chart (de page laadt hem in een nieuwe tab). */
  readonly chartOpenRequested = output<string>();
  /** Openen vanuit de sectie-kalender: chart in een nieuwe tab mét het Notities-paneel (spec §2.9). */
  readonly chartOpenWithNotesRequested = output<string>();
  /** Tijdlijn-knop → de levenstijdlijn-pagina van deze chart (stap 5). */
  readonly timelineRequested = output<string>();
  /** Brein-knop → het paneel "Mijn mind zegt" van deze chart (spec 2026-10-22-mind-logboek §5). */
  readonly mindLogRequested = output<string>();
  /** Na een geslaagde toevoeging → het paneel herlaadt de relaties. */
  readonly changed = output<void>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly library = inject(HdChartLibraryService);
  private readonly ichingMoments = inject(HdIChingMomentsService);
  private readonly ichingAccess = inject(HdIChingAccessService);

  /** I Tjing-momenten van deze chart (raadplegingen uit het dagboek), enkel in de sectie-kalender. */
  private readonly ichingMarkers = signal<HdLifeTimelineMarker[]>([]);
  private ichingLoadSeq = 0;
  /** Doel van de I Tjing-momentdialoog (lezen of nieuw); null = gesloten. */
  protected readonly ichingDialogTarget = signal<HdIChingMomentDialogTarget | null>(null);

  /** Sectie is standaard dichtgeklapt (zelfde patroon als Relaties). */
  protected readonly expanded = signal(false);
  /** Kalenderweergave van de momenten in de sectie (beslispunt 10, spec §2.7). */
  protected readonly calendarView = signal(false);
  /** Target van de toevoeg-dialog (de "+"-knop); null = gesloten. */
  protected readonly linkDialogTarget = signal<HdMomentLinkTarget | null>(null);
  /** Datum-prefill + categorie-preselectie van de dialog (kalender-add = daily). */
  protected readonly linkDialogPrefillUtc = signal<string | null>(null);
  protected readonly linkDialogCategory = signal<string | null>(null);
  /** Smaak per ingang: de brein-knop opent in Gedachte; de plus en de kalender nemen de standaard (Gedachte & dagboek). */
  protected readonly linkDialogInitialMode = signal<HdMomentLinkMode | null>(null);
  /** Target van de alle-momenten-dialog (zoeken + paginering); null = gesloten. */
  protected readonly momentsDialogTarget = signal<HdMomentsDialogTarget | null>(null);
  /** Markdown-export van alle momenten van deze chart (spec 2026-10-21-dagboek-momenten-export-design). */
  protected readonly exportTarget = signal<HdMomentsExportTarget | null>(null);

  protected readonly text = computed(() => {
    const translate = this.contentTranslate();
    return {
      title: translate('humanDesign.advancedPanel.moments.title'),
      saveFirst: translate('humanDesign.advancedPanel.saveFirst'),
      sharedUnavailable: translate('humanDesign.advancedPanel.sharedUnavailable'),
      addMoment: translate('humanDesign.advancedPanel.moments.addMoment'),
      timeline: translate('humanDesign.advancedPanel.moments.timeline'),
      calendar: translate('humanDesign.advancedPanel.moments.calendar'),
      list: translate('humanDesign.advancedPanel.moments.list'),
      edit: translate('humanDesign.lifeTimeline.edit'),
      none: translate('humanDesign.advancedPanel.moments.none'),
      loading: translate('humanDesign.chartManager.detail.loading'),
      show: translate('humanDesign.advancedPanel.moments.show'),
      hide: translate('humanDesign.advancedPanel.moments.hide'),
      showAll: translate('humanDesign.advancedPanel.moments.showAll'),
      momentOfTitle: translate('humanDesign.advancedPanel.moments.momentOfTitle'),
      hasNote: translate('humanDesign.advancedPanel.moments.hasNote'),
      hasAttachments: translate('humanDesign.advancedPanel.moments.hasAttachments'),
      export: translate('humanDesign.momentsExport.button'),
      mindLog: translate('humanDesign.advancedPanel.moments.mindLog'),
      addMind: translate('humanDesign.advancedPanel.moments.addMind'),
    };
  });

  /** Aantal mind-notities van deze chart; de brein-knop toont enkel wanneer er zijn. */
  protected readonly mindCount = computed(() => this.relations().filter(relation => isHdMindMomentRelation(relation)).length);

  /**
   * Levenstijdlijn-knop alleen op standaard charts (spec levenstijdlijn §2.6):
   * afgeleide presets (cycle/composite/transit) hebben geen eigen levenslijn.
   * Momentlinks zelf blijven op álle charttypes werken (beslispunt 10 braindump).
   */
  protected readonly timelineAvailable = computed(() => {
    const preset = this.preset();
    return preset != null && preset.cycle == null && preset.composite == null && preset.transit == null;
  });

  protected readonly momentRows = computed<HdMomentRowVm[]>(() => buildHdMomentRows(this.relations()));

  /** Omgekeerde richting: deze chart ís een moment van … (kind-chart-scenario). */
  protected readonly momentOfRows = computed<HdMomentRowVm[]>(() =>
    this.relations()
      .filter(relation => relation.relationType === 'momentOf')
      .map(relation => buildHdMomentRow(relation)),
  );

  protected readonly visibleMomentRows = computed<HdMomentRowVm[]>(() =>
    this.momentRows().slice(0, COMPACT_ROW_COUNT),
  );

  /** Markers voor de sectie-kalender (gedeelde mapping met de tijdlijn-pagina), plus de I Tjing-momenten. */
  protected readonly calendarMarkers = computed<HdLifeTimelineMarker[]>(() => [
    ...buildHdLifeTimelineMarkers(this.momentRows()),
    ...this.ichingMarkers(),
  ]);

  constructor() {
    // Chart gewisseld, raadpleging bewaard/verwijderd of toegang gewijzigd → I Tjing-markers herladen.
    effect(() => {
      const chartId = this.savedChartId();
      this.ichingMoments.changed();
      this.ichingAccess.classicVisible();
      untracked(() => void this.loadIChingMarkers(chartId));
    });
  }

  private async loadIChingMarkers(chartId: string | null): Promise<void> {
    const seq = ++this.ichingLoadSeq;
    if (!chartId) {
      this.ichingMarkers.set([]);
      return;
    }
    try {
      const markers = await this.ichingMoments.markersForChart(chartId);
      if (seq === this.ichingLoadSeq) this.ichingMarkers.set(markers);
    } catch {
      // Optionele laag: een fout laat de gewone momenten staan.
      if (seq === this.ichingLoadSeq) this.ichingMarkers.set([]);
    }
  }

  /** "Openen" op een I Tjing-moment in de sectie-kalender: de raadpleging in de dialoog. */
  protected openConsultation(consultationId: string): void {
    this.ichingDialogTarget.set({ kind: 'view', consultationId, chartName: this.chartName() });
  }

  /** Derde keuze in de momentdialoog: nieuwe raadpleging voor deze chart op dat tijdstip. */
  protected onIChingRequested(request: HdIChingConsultRequest): void {
    this.ichingDialogTarget.set({ kind: 'new', ...request });
  }

  protected readonly hiddenRowCount = computed(() =>
    Math.max(0, this.momentRows().length - COMPACT_ROW_COUNT),
  );

  protected toggleExpanded(): void {
    this.expanded.update(expanded => !expanded);
  }

  /**
   * Weergaveknoppen (finetune 2026-09-21): expliciete lijst- en kalenderknop
   * i.p.v. één toggle; een klik klapt een dichtgeklapte sectie meteen open.
   */
  protected showListView(): void {
    this.calendarView.set(false);
    this.expanded.set(true);
  }

  protected showCalendarView(): void {
    this.calendarView.set(true);
    this.expanded.set(true);
  }

  protected openRow(row: HdMomentRowVm): void {
    if (row.relation.otherChartId) this.chartOpenRequested.emit(row.relation.otherChartId);
  }

  /** "+"-knop: toevoeg-dialog (bestaande chart of moment op datum) voor de actieve chart. */
  protected openExport(): void {
    const chartId = this.savedChartId();
    if (!chartId) return;
    this.exportTarget.set({ kind: 'chart', chartId, chartName: this.chartName() });
  }

  protected openLinkDialog(): void {
    const chartId = this.savedChartId();
    if (!chartId) return;
    this.linkDialogPrefillUtc.set(null);
    this.linkDialogCategory.set(null);
    this.linkDialogInitialMode.set(null);
    this.linkDialogTarget.set({ chartId, chartName: this.chartName() });
  }

  /** Brein-knop zonder notities: meteen een eerste mind-notitie toevoegen (categorie mind voorgeselecteerd). */
  protected openMindAdd(): void {
    const chartId = this.savedChartId();
    if (!chartId) return;
    this.linkDialogPrefillUtc.set(moment().toISOString());
    this.linkDialogCategory.set(HD_MIND_MOMENT_CATEGORY);
    this.linkDialogInitialMode.set('thought');
    this.linkDialogTarget.set({ chartId, chartName: this.chartName() });
  }

  /** "+ moment" vanuit de sectie-kalender: dag voorgevuld en categorie daily (spec §2.7). */
  protected openCalendarAdd(dateUtc: string): void {
    const chartId = this.savedChartId();
    if (!chartId) return;
    this.linkDialogPrefillUtc.set(dateUtc);
    this.linkDialogCategory.set('daily');
    this.linkDialogInitialMode.set(null);
    this.linkDialogTarget.set({ chartId, chartName: this.chartName() });
  }

  /** "Alle momenten…": volledige lijst met zoeken + paginering. */
  protected openMomentsDialog(): void {
    this.momentsDialogTarget.set({ chartName: this.chartName() });
  }

  protected onLinkChanged(): void {
    this.changed.emit();
  }

  // ── Moment bewerken (finetune 2026-09-21) ──────────────────────────────────

  /** Doel van de editdialoog (geladen document); null = gesloten. */
  protected readonly editDialogTarget = signal<HdChartDocumentResult | null>(null);
  protected readonly editError = signal('');

  /** Potlood op een rij of in de sectie-kalender: document laden en editdialoog openen. */
  protected async openMomentEdit(chartId: string): Promise<void> {
    this.editError.set('');
    try {
      this.editDialogTarget.set(await this.library.getChartDocument(chartId));
    } catch (e) {
      this.editError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Opslaan uit de editdialoog: persisteren en het paneel laten verversen. */
  protected async onMomentEditSaved(input: HdChartUpdateInput): Promise<void> {
    this.editError.set('');
    try {
      await this.library.updateChart(input);
      this.changed.emit();
    } catch (e) {
      this.editError.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  private contentTranslate(): (key: string) => string {
    this.contentLang.translationsLoadedTick();
    const lang = this.contentLang.effectiveLang();
    return (key: string) => this.transloco.translate<string>(key, undefined, lang);
  }
}
